import { lazy, Suspense, useState, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import {
  Root,
  RequireAuth,
  RequireOnboarded,
  RequireProfile,
  PublicOnly,
  RootRedirect,
  SetupStepGuard,
} from './routes/guards';
import { AppLayout } from './components/layout/app-layout';
import { RouteError } from './routes/route-error';
import {
  DetailSkeleton,
  FeedSkeleton,
  FriendsSkeleton,
  GridPageSkeleton,
  ListPageSkeleton,
  ScreenSkeleton,
  SettingsSectionSkeleton,
  SettingsSkeleton,
  StatsSkeleton,
} from './components/skeletons';
import { appPages, type PageLoader } from './lib/preload';

/**
 * A page whose code loads when it's first opened. The address changes at once and the page's
 * skeleton shows straight away while its code arrives, instead of the old page waiting. Pages in
 * the app (`preload`) are fetched in the background once the app is open, so they usually open
 * instantly.
 */
const page = (loader: PageLoader, Fallback: ComponentType = ScreenSkeleton, preload = true) => {
  let loaded: ComponentType | null = null;
  const load: PageLoader = () =>
    loader().then((m) => {
      loaded = m.default;
      return m;
    });
  const Lazy = lazy(load);
  // Once its code is in, the page is drawn directly: going through lazy() would show the
  // skeleton for a moment anyway, and React then holds it on screen for up to 300ms. Chosen
  // once per visit, so the page is never swapped for a copy of itself while open.
  function Page() {
    const [Component] = useState(() => loaded ?? Lazy);
    return <Component />;
  }
  if (preload) appPages.push(load);
  return {
    element: (
      <Suspense fallback={<Fallback />}>
        <Page />
      </Suspense>
    ),
  };
};

const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <RootRedirect /> },
      { path: 'offline', ...page(() => import('./routes/offline'), ScreenSkeleton, false) },
      { path: 'privacy', ...page(() => import('./routes/privacy'), ScreenSkeleton, false) },
      {
        path: 'auth/discord',
        ...page(() => import('./routes/discord-return'), ScreenSkeleton, false),
      },
      {
        path: 'offline/watch/:id',
        ...page(() => import('./routes/offline-watch'), ScreenSkeleton, false),
      },
      {
        element: <PublicOnly />,
        children: [
          { path: 'welcome', ...page(() => import('./routes/welcome'), ScreenSkeleton, false) },
          { path: 'login', ...page(() => import('./routes/login'), ScreenSkeleton, false) },
          {
            path: 'register',
            ...page(() => import('./routes/setup/account'), ScreenSkeleton, false),
          },
          {
            path: 'forgot-password',
            ...page(() => import('./routes/forgot-password'), ScreenSkeleton, false),
          },
          {
            path: 'reset-password',
            ...page(() => import('./routes/reset-password'), ScreenSkeleton, false),
          },
          {
            path: 'setup/account',
            ...page(() => import('./routes/setup/account'), ScreenSkeleton, false),
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            path: 'setup',
            element: <SetupStepGuard />,
            children: [
              {
                path: 'profiles',
                ...page(() => import('./routes/setup/profiles'), ScreenSkeleton, false),
              },
              {
                path: 'provider',
                ...page(() => import('./routes/setup/provider'), ScreenSkeleton, false),
              },
              {
                path: 'addons',
                ...page(() => import('./routes/setup/addons'), ScreenSkeleton, false),
              },
              {
                path: 'preferences',
                ...page(() => import('./routes/setup/preferences'), ScreenSkeleton, false),
              },
              {
                path: 'complete',
                ...page(() => import('./routes/setup/complete'), ScreenSkeleton, false),
              },
            ],
          },
          {
            element: <RequireOnboarded />,
            children: [
              { path: 'profiles', ...page(() => import('./routes/profiles'), ScreenSkeleton) },
              {
                path: 'profiles/manage',
                ...page(() => import('./routes/profiles-manage'), ScreenSkeleton),
              },
              {
                element: <RequireProfile />,
                children: [
                  // The player itself is drawn by PlayerHost (in RequireProfile), so it can carry on
                  // as the mini player when you leave these.
                  { path: 'watch/movie/:id', element: null },
                  { path: 'watch/series/:id/:season/:episode', element: null },
                  {
                    element: <AppLayout />,
                    children: [
                      { path: 'home', ...page(() => import('./routes/home'), FeedSkeleton) },
                      {
                        path: 'movies',
                        ...page(() => import('./routes/browse-movies'), FeedSkeleton),
                      },
                      { path: 'tv', ...page(() => import('./routes/browse-tv'), FeedSkeleton) },
                      {
                        path: 'discover',
                        ...page(() => import('./routes/discover'), GridPageSkeleton),
                      },
                      {
                        path: 'search',
                        ...page(() => import('./routes/search'), GridPageSkeleton),
                      },
                      {
                        path: 'movie/:id',
                        ...page(() => import('./routes/movie'), DetailSkeleton),
                      },
                      {
                        path: 'series/:id',
                        ...page(() => import('./routes/series'), DetailSkeleton),
                      },
                      {
                        path: 'party/:code',
                        ...page(() => import('./routes/party'), DetailSkeleton),
                      },
                      {
                        path: 'friends',
                        ...page(() => import('./routes/friends'), FriendsSkeleton),
                      },
                      {
                        path: 'downloads',
                        ...page(() => import('./routes/downloads'), ListPageSkeleton),
                      },
                      { path: 'stats', ...page(() => import('./routes/stats'), StatsSkeleton) },
                      {
                        path: 'title/imdb/:imdbId',
                        ...page(() => import('./routes/imdb-redirect'), DetailSkeleton),
                      },
                      {
                        path: 'library',
                        ...page(() => import('./routes/library'), GridPageSkeleton),
                      },
                      {
                        path: 'collection',
                        ...page(() => import('./routes/collection'), GridPageSkeleton),
                      },
                      { path: 'my-list', element: <Navigate to="/library?tab=saved" replace /> },
                      {
                        path: 'history',
                        ...page(() => import('./routes/history'), ListPageSkeleton),
                      },
                      {
                        path: 'addons',
                        ...page(() => import('./routes/addons'), ListPageSkeleton),
                      },
                      {
                        path: 'settings',
                        ...page(() => import('./routes/settings/layout'), SettingsSkeleton),
                        children: [
                          {
                            index: true,
                            ...page(
                              () => import('./routes/settings/index'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'account',
                            ...page(
                              () => import('./routes/settings/account'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'profiles',
                            ...page(
                              () => import('./routes/settings/profiles'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'streaming',
                            ...page(
                              () => import('./routes/settings/streaming'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'playback',
                            ...page(
                              () => import('./routes/settings/playback'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'addons',
                            ...page(
                              () => import('./routes/settings/addons'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'subtitles',
                            ...page(
                              () => import('./routes/settings/subtitles'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'appearance',
                            ...page(
                              () => import('./routes/settings/appearance'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'history',
                            ...page(
                              () => import('./routes/settings/history'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'preferences',
                            ...page(
                              () => import('./routes/settings/preferences'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'downloads',
                            ...page(
                              () => import('./routes/settings/downloads'),
                              SettingsSectionSkeleton,
                            ),
                          },
                          {
                            path: 'updates',
                            ...page(
                              () => import('./routes/settings/updates'),
                              SettingsSectionSkeleton,
                            ),
                          },
                        ],
                      },
                      { path: '*', ...page(() => import('./routes/not-found'), ScreenSkeleton) },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', ...page(() => import('./routes/not-found'), ScreenSkeleton) },
    ],
  },
];

export const router = createBrowserRouter(routes);
